import { useState, useRef, useEffect, Component } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { isNil, truncate } from 'lodash'
import cx from 'classnames'

import { useUploadImage } from 'hooks'
import {
  useCreateThreadMutation,
  useMovePostToThreadMutation,
  useUpdateThreadMutation,
} from 'store/api/yay/thread'
import { showErrorPopup } from 'store/actions/popup'
import { ERROR_POPUP } from 'features/GlobalErrorPopup/config'
import { selectThreadBoxModal, toggleThreadBoxModal } from 'store/actions'
import { Attachment } from 'lib/_types'

import './style.scss'

const ThreadBoxModal = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const threadBoxModal = useSelector(selectThreadBoxModal)

  const { groupId } = useParams()
  const { upload, isLoading: uploadLoading } = useUploadImage()

  const [createThread, createThreadRes] = useCreateThreadMutation()
  const [updateThread, updateThreadRes] = useUpdateThreadMutation()
  const [movePostToThread, movePostToThreadRes] = useMovePostToThreadMutation()

  const [__file, __fileSet] = useState<File>(null)
  const [__threadTitle, __threadTitleSet] = useState('')

  const ___$input = useRef<HTMLInputElement>()

  const isActive = threadBoxModal?.isActive
  const isEditing = threadBoxModal?.mode == 'edit'

  useEffect(() => {
    if (isActive) {
      __threadTitleSet(threadBoxModal?.thread?.title ?? '')
    }
  }, [isActive])

  useEffect(() => {
    if (isActive) {
      window.setTimeout(() => {
        ___$input?.current?.focus?.()
      }, 100)
    }
  }, [isActive])

  return (
    <div
      className={cx('Modal', {
        'Modal--active': isActive,
      })}
    >
      <a
        className="Modal__overlay"
        onClick={() => {
          dispatch(toggleThreadBoxModal())
        }}
      />

      <div className="Modal__wrapper Modal__wrapper--auto-h">
        <div className="Modal__header Modal__header--bdrz-t">
          <a
            className="Modal__header__close"
            onClick={() => {
              dispatch(toggleThreadBoxModal())
            }}
          />

          <h2 className="Modal__header__h2">
            {isEditing
              ? t('group_threads_info_page.group_thread_info_setting')
              : t('group_threads_page.group_thread_create_title')}
          </h2>
        </div>

        <div className="Modal__body Modal__body--ov-v">
          {(() => {
            const canEdit = !isEditing || threadBoxModal?.canEdit

            return (
              <form
                className={cx('GroupThreads__create-thread', {
                  'GroupThreads__create-thread--x-disabled':
                    createThreadRes?.isLoading ||
                    updateThreadRes?.isLoading ||
                    movePostToThreadRes?.isLoading ||
                    uploadLoading,
                })}
                onSubmit={async (e) => {
                  e.preventDefault()

                  let postMethod:
                    | typeof createThread
                    | typeof updateThread
                    | typeof movePostToThread = createThread

                  let threadData = {
                    groupId,
                    title: __threadTitle,
                    threadIconFilename: null,
                    threadId: null,
                    postId: null,
                  }

                  if (!isNil(__file)) {
                    let resUpload: Array<Attachment>

                    try {
                      resUpload = await upload([__file], 'group_thread_icon')
                    } catch {
                      return
                    }

                    threadData = {
                      ...threadData,
                      threadIconFilename: resUpload?.[0]?.fileName,
                    }
                  }

                  if (
                    threadBoxModal?.mode == 'edit' &&
                    threadBoxModal?.thread
                  ) {
                    postMethod = updateThread

                    threadData = {
                      ...threadData,
                      threadId: threadBoxModal?.thread?.id,
                    }
                  }

                  if (
                    threadBoxModal?.mode == 'move' &&
                    threadBoxModal?.postIdToMove
                  ) {
                    postMethod = movePostToThread

                    threadData = {
                      ...threadData,
                      postId: threadBoxModal?.postIdToMove,
                    }
                  }

                  postMethod(threadData).then((res: any) => {
                    if (!isNil(res?.['data']?.id)) {
                      __threadTitleSet('')
                      __fileSet(null)
                      dispatch(toggleThreadBoxModal())
                      threadBoxModal?.onCreated?.({ thread: res?.['data'] })
                    }
                  })
                }}
              >
                <div className="GroupThreads__create-thread__body">
                  {!isEditing && (
                    <label className="GroupThreads__create-thread__body__file">
                      <GroupThreadImage __file={__file} />

                      <div
                        className={cx(
                          'GroupThreads__create-thread__body__file__icon',
                          {
                            'GroupThreads__create-thread__body__file__icon--with-file':
                              !isNil(__file),
                          }
                        )}
                      ></div>

                      <input
                        type="file"
                        className="GroupThreads__create-thread__body__file__input"
                        accept=".jpg, .jpeg, .png, .gif"
                        onChange={(e) => {
                          const _file = e?.target?.files?.[0]

                          if (
                            _file?.size > 10 * 1024 * 1024 ||
                            (_file?.type == 'image/gif' &&
                              _file?.size > 10 * 1024 * 512)
                          ) {
                            dispatch(
                              showErrorPopup(
                                ERROR_POPUP.FILE_SIZE_OVER_MAX_LIMIT
                              )
                            )
                            return false
                          }

                          __fileSet(_file ?? __file)
                        }}
                      />
                    </label>
                  )}

                  <label className="GroupThreads__create-thread__body__label">
                    <h3 className="GroupThreads__create-thread__body__h3">
                      {isEditing
                        ? t('group_threads_page.group_thread_create_hint')
                        : t('group_threads_page.group_thread_create_title')}
                    </h3>

                    <input
                      disabled={!canEdit}
                      type="text"
                      className="GroupThreads__create-thread__body__input"
                      value={__threadTitle}
                      placeholder={t(
                        'group_threads_page.group_thread_create_hint'
                      )}
                      ref={___$input}
                      onChange={(e) => {
                        __threadTitleSet(
                          truncate(e?.target?.value, {
                            length: 50,
                            omission: '',
                          })
                        )
                      }}
                    />
                  </label>
                </div>

                {canEdit && (
                  <div className="GroupThreads__create-thread__footer">
                    <button
                      className={cx('Button', {
                        'Button--secondary': true,
                        'Button--icon-plus': !isEditing,
                      })}
                      disabled={__threadTitle?.length == 0}
                    >
                      {isEditing
                        ? t('common.save')
                        : t('group_threads_page.group_thread_create_button')}
                    </button>
                  </div>
                )}
              </form>
            )
          })()}
        </div>
      </div>
    </div>
  )
}

export default ThreadBoxModal

type GroupThreadImageProps = {
  __file: null | File
}
class GroupThreadImage extends Component<GroupThreadImageProps, {}> {
  shouldComponentUpdate = (nextProps: GroupThreadImageProps) => {
    return this.props?.__file != nextProps?.__file
  }

  render = () => {
    return (
      <svg
        className="GroupThreads__create-thread__body__file__svg"
        viewBox="0 0 1 1"
        style={{
          backgroundImage: this.props?.__file
            ? `url(${URL.createObjectURL(this.props?.__file)})`
            : null,
        }}
      />
    )
  }
}
